/**
 * 平台上下文 Provider
 * 
 * 提供全局的平台信息，避免每个组件都调用 usePlatform
 */

import { createContext, useContext, ReactNode } from 'react'
import { usePlatform, PlatformInfo } from './usePlatform'

// 创建上下文
const PlatformContext = createContext<PlatformInfo | null>(null)

interface PlatformProviderProps {
  children: ReactNode
}

/**
 * 平台 Provider
 * 
 * @example
 * ```tsx
 * // App.tsx
 * function App() {
 *   return (
 *     <PlatformProvider>
 *       <AppContent />
 *     </PlatformProvider>
 *   )
 * }
 * ```
 */
export function PlatformProvider({ children }: PlatformProviderProps) {
  const platform = usePlatform()

  return (
    <PlatformContext.Provider value={platform}>
      {children}
    </PlatformContext.Provider>
  )
}

/**
 * 使用平台上下文
 * 
 * 比直接使用 usePlatform 更高效，因为所有组件共享同一个状态
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isMobile, safeAreaInsets } = usePlatformContext()
 *   
 *   return (
 *     <div style={{ paddingTop: safeAreaInsets.top }}>
 *       {isMobile ? 'Mobile' : 'Desktop'}
 *     </div>
 *   )
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePlatformContext(): PlatformInfo {
  const context = useContext(PlatformContext)
  
  if (!context) {
    throw new Error('usePlatformContext must be used within a PlatformProvider')
  }
  
  return context
}

// 导出上下文供高级用例使用
export { PlatformContext }

